import { useEffect, useMemo, useState } from 'react';
import { DoroPartyWordmark } from '../../../shared/ui/DoroPartyWordmark.jsx';

const SESSION_KEY = 'doroparty:install-guide-dismissed';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.navigator.standalone === true;
}

function readPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const agent = String(navigator.userAgent || '');
  const isIos = /iPhone|iPad|iPod/i.test(agent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIos) return 'ios';
  if (/Android/i.test(agent)) return 'android';
  return 'other';
}

function sessionDismissed() {
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberDismissed() {
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Storage can be unavailable in private browsing; closing still works for this view.
  }
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="5" cy="12" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="19" cy="12" r="1.7" />
    </svg>
  );
}

function VerticalMoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="5" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="12" cy="19" r="1.7" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 15V3m0 0L8 7m4-4 4 4" />
      <path d="M7 10H5.8A2.8 2.8 0 0 0 3 12.8v5.4A2.8 2.8 0 0 0 5.8 21h12.4a2.8 2.8 0 0 0 2.8-2.8v-5.4a2.8 2.8 0 0 0-2.8-2.8H17" />
    </svg>
  );
}

function HomeScreenIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

function AddIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function InstallIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <path d="M12 6v8m0 0-3-3m3 3 3-3M8 18h8" />
    </svg>
  );
}

const IOS_STEPS = [
  { label: '더 보기', detail: 'Safari의 … 버튼', icon: <MoreIcon /> },
  { label: '공유', detail: '공유 메뉴 열기', icon: <ShareIcon /> },
  { label: '홈 화면에 추가', detail: '목록을 내려 선택', icon: <HomeScreenIcon /> },
  { label: '추가', detail: '오른쪽 위에서 완료', icon: <AddIcon /> },
];

const ANDROID_STEPS = [
  { label: '메뉴', detail: 'Chrome의 ⋮ 버튼', icon: <VerticalMoreIcon /> },
  { label: '설치 및 바로가기 만들기', detail: '메뉴에서 선택', icon: <HomeScreenIcon /> },
  { label: '설치', detail: '설치 창에서 확인', icon: <InstallIcon /> },
];

function InstallSteps({ steps, label }) {
  return (
    <div className={`mobile-install-steps columns-${steps.length}`} aria-label={label}>
      {steps.map((step, index) => (
        <div className="mobile-install-step" key={step.label}>
          <span className="mobile-install-step-number">{index + 1}</span>
          <span className="mobile-install-step-icon">{step.icon}</span>
          <strong>{step.label}</strong>
          <small>{step.detail}</small>
        </div>
      ))}
    </div>
  );
}

function IosInstallGuide() {
  return (
    <div className="mobile-install-platform-guide">
      <InstallSteps steps={IOS_STEPS} label="iPhone 홈 화면 추가 순서" />
      <p>공유 버튼이 화면에 바로 보이는 Safari 배치에서는 첫 단계를 건너뛰세요. 설치 화면에서 ‘웹 앱으로 열기’가 표시되면 켠 뒤 ‘추가’를 누릅니다.</p>
    </div>
  );
}

function AndroidInstallGuide({ hasInstallEvent }) {
  return (
    <div className="mobile-install-platform-guide">
      <InstallSteps steps={ANDROID_STEPS} label="Android Chrome 앱 설치 순서" />
      <p>{hasInstallEvent
        ? '아래 ‘앱 설치’ 버튼을 누르면 메뉴 단계를 건너뛰고 바로 설치할 수 있습니다.'
        : 'Chrome 기준입니다. 메뉴에서 ‘설치 및 바로가기 만들기’를 누른 뒤 ‘설치’를 선택하세요. 버전에 따라 ‘앱 설치’ 또는 ‘홈 화면에 추가’로 표시될 수 있습니다.'}</p>
    </div>
  );
}

export function MobileInstallPrompt() {
  const platform = useMemo(readPlatform, []);
  const [visible, setVisible] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandalone() || sessionDismissed()) return undefined;

    const showTimer = window.setTimeout(() => setVisible(true), 700);
    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setInstallEvent(event);
      setVisible(true);
    };
    const handleInstalled = () => {
      rememberDismissed();
      setVisible(false);
      setInstallEvent(null);
    };
    const displayMode = window.matchMedia?.('(display-mode: standalone)');
    const handleDisplayMode = () => {
      if (isStandalone()) handleInstalled();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    displayMode?.addEventListener?.('change', handleDisplayMode);

    return () => {
      window.clearTimeout(showTimer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
      displayMode?.removeEventListener?.('change', handleDisplayMode);
    };
  }, []);

  const close = () => {
    rememberDismissed();
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) {
      close();
      return;
    }
    setInstalling(true);
    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice?.outcome === 'accepted') close();
    } finally {
      setInstalling(false);
      setInstallEvent(null);
    }
  };

  if (!visible || isStandalone()) return null;

  const instruction = installEvent
    ? '아래 앱 설치 버튼을 누르고 브라우저 설치 창에서 확인하세요.'
    : '브라우저 메뉴에서 “설치 및 바로가기 만들기”를 누른 뒤 “설치”를 선택하세요. 버전에 따라 “앱 설치” 또는 “홈 화면에 추가”로 표시될 수 있습니다.';

  return (
    <div className="mobile-install-prompt-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section className="mobile-install-prompt" role="dialog" aria-modal="true" aria-labelledby="mobile-install-prompt-title">
        <header>
          <DoroPartyWordmark compact />
          <button type="button" onClick={close} aria-label="설치 안내 닫기">×</button>
        </header>
        <div className="mobile-install-prompt-copy">
          <span>APP INSTALL</span>
          <h2 id="mobile-install-prompt-title">도로파티를 홈 화면에 추가하세요</h2>
          <p>브라우저 탭 없이 앱처럼 열 수 있습니다. 푸시 알림은 설치 후 알림 권한을 허용해야 받을 수 있습니다.</p>
        </div>
        <div className={`mobile-install-prompt-guide ${platform}`}>
          <b>{platform === 'ios' ? 'iPhone·iPad Safari' : platform === 'android' ? 'Android Chrome' : '모바일 브라우저'}</b>
          {platform === 'ios'
            ? <IosInstallGuide />
            : platform === 'android'
              ? <AndroidInstallGuide hasInstallEvent={Boolean(installEvent)} />
              : <p>{instruction}</p>}
        </div>
        <footer>
          <button type="button" className="secondary" onClick={close}>나중에</button>
          <button type="button" className="primary" disabled={installing} onClick={install}>
            {installEvent ? (installing ? '설치 중…' : '앱 설치') : '확인'}
          </button>
        </footer>
      </section>
    </div>
  );
}
